// @ts-nocheck
/**
 * Educational ROI Calculator Component (Epic 18)
 * Enhanced calculator with real-time updates, progressive validation, and lead capture
 * Uses Carbon Design System for accessible, compliant UI with <10ms calculation responses
 * Features: Interactive demos, AI personalization, A/B testing integration, PDF reports
 */

import React, { useState, useMemo, useCallback } from 'react';
import {
  Grid,
  Column,
  Form,
  FormGroup,
  NumberInput,
  Select,
  SelectItem,
  Button,
  Tile,
  Heading,
  Stack,
  ProgressIndicator,
  ProgressStep,
  Tag,
  InlineNotification,
  Modal,
  TextInput,
  Checkbox,
  Accordion,
  AccordionItem,
} from '@carbon/react';
import { Calculator, AnalyticsReference, ArrowRight, Download, Email, Calendar } from '@carbon/icons-react';

import ROIVisualization from './ROIVisualization';
import { useROICalculation } from '../../hooks/useROICalculation';
import { EducationalLeadCapture } from '../../../shared/interfaces/EducationalCalculators';
import { generateAndDownloadReport } from '../../utils/pdfReportGenerator';
import styles from './EducationalROICalculator.module.scss';

// Spinner styles now handled by CSS module

// Debounce utility for real-time calculations
function debounce<T extends (...args: any[]) => any>(func: T, wait: number): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout;
  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
}

interface ROICalculatorProps {
  calculatorType:
    | 'student-performance'
    | 'teacher-productivity'
    | 'cost-savings'
    | 'learning-outcomes'
    | 'technology-investment'
    | 'parent'
    | 'institution'
    | 'tutor'
    | 'student'
    | 'competitor-comparison';
  onLeadCapture?: (leadData: LeadCaptureData) => void;
  onDemoRequest?: (demoData: DemoRequestData) => void;
  onComplete?: (results: any) => void;
  enableABTesting?: boolean;
  testId?: string;
  visitorId?: string;
  institutionType?: string;
  enableWorkersAI?: boolean;
  onConversionEvent?: (eventType: string, data?: any) => void;
}

interface LeadCaptureData {
  name: string;
  email: string;
  title: string;
  institution: string;
  phone?: string;
  calculatorType: string;
  roiResults: ROIResults;
}

interface DemoRequestData {
  name: string;
  email: string;
  institution: string;
  preferredDate: string;
  calculatorType: string;
}

interface ROIInputs {
  // Student Performance Inputs
  currentStudentCount?: number;
  currentGradingHours?: number;
  averageTeacherSalary?: number;

  // Teacher Productivity Inputs
  teacherCount?: number;
  avgClassSize?: number;
  gradingTimePerStudent?: number;
  lessonPlanningHours?: number;

  // Cost Savings Inputs
  annualTechnologyBudget?: number;
  textbookCostPerStudent?: number;
  professionalDevelopmentCost?: number;
  infrastructureCost?: number;

  // Learning Outcomes Inputs
  currentTestScores?: number;
  targetImprovement?: number;
  interventionCost?: number;

  // Technology Investment Inputs
  implementationCost?: number;
  annualLicenseCost?: number;
  trainingCost?: number;
  maintenanceCost?: number;
}

interface ROIResults {
  annualSavings: number;
  threeYearROI: number;
  paybackPeriod: number;
  productivityGains: number;
  studentOutcomeImprovement: number;
  costPerStudent: number;
  breakdownByCategory: {
    [key: string]: number;
  };
}

const EducationalROICalculator: React.FC<ROICalculatorProps> = ({
  calculatorType,
  onLeadCapture,
  onDemoRequest,
  onComplete,
  enableABTesting = false,
  testId,
  visitorId,
  institutionType,
  enableWorkersAI = false,
  onConversionEvent,
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [inputs, setInputs] = useState<ROIInputs>({});
  const [showResults, setShowResults] = useState(false);
  const [showLeadCapture, setShowLeadCapture] = useState(false);
  const [showDemoRequest, setShowDemoRequest] = useState(false);
  const [showInteractiveDemo, setShowInteractiveDemo] = useState(false);
  const [leadData, setLeadData] = useState<Partial<LeadCaptureData>>({});
  const [selectedRole, setSelectedRole] = useState<string>('');
  const [demoData, setDemoData] = useState<Partial<DemoRequestData>>({});
  const [personalizedContent, setPersonalizedContent] = useState<any>(null);
  const [abTestVariant, setABTestVariant] = useState<string>('control');
  const [conversionTracking, setConversionTracking] = useState<any[]>([]);

  // Use enhanced ROI calculation hook with real-time capabilities
  const { calculateROI, calculateROIAsync, calculateROIRealtime, loading, error } = useROICalculation();

  // Real-time calculation state
  const [realtimeResults, setRealtimeResults] = useState<ROIResults | null>(null);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [isCalculating, setIsCalculating] = useState(false);

  // Enhanced functionality for Epic 18

  // A/B Testing Integration
  React.useEffect(() => {
    if (enableABTesting && testId && visitorId) {
      fetchABTestVariant();
    }
  }, [enableABTesting, testId, visitorId]);

  const fetchABTestVariant = async () => {
    try {
      const response = await fetch(`/api/v1/ab-tests/assign?testId=${testId}&visitorId=${visitorId}`);
      const data = await response.json();
      setABTestVariant(data.variant || 'control');

      // Track A/B test assignment
      trackConversionEvent('ab_test_assigned', {
        testId,
        variant: data.variant,
        calculatorType,
      });
    } catch (error) {
      console.warn('A/B test variant fetch failed:', error);
      setABTestVariant('control');
    }
  };

  // Workers AI Personalization
  React.useEffect(() => {
    if (enableWorkersAI && institutionType) {
      loadPersonalizedContent();
    }
  }, [enableWorkersAI, institutionType, calculatorType]);

  const loadPersonalizedContent = async () => {
    try {
      const visitorContext = {
        institutionType,
        calculatorType,
        detectedRole: 'administrator', // Would be determined by AI
      };

      const response = await fetch(
        `/api/marketing/content/roi-calculator?visitor_context=${encodeURIComponent(JSON.stringify(visitorContext))}`
      );
      const data = await response.json();
      setPersonalizedContent(data.content);
    } catch (error) {
      console.warn('Personalization failed:', error);
    }
  };

  // Enhanced Conversion Tracking
  const trackConversionEvent = (eventType: string, data?: any) => {
    const eventData = {
      eventType,
      visitorId,
      variant: abTestVariant,
      testId,
      eventData: data,
      timestamp: new Date().toISOString(),
      calculatorType,
      institutionType,
    };

    setConversionTracking(prev => [...prev, eventData]);

    // Send to analytics endpoint
    fetch('/api/marketing/analytics/conversion', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(eventData),
    }).catch(error => console.warn('Conversion tracking failed:', error));

    // Callback to parent component
    if (onConversionEvent) {
      onConversionEvent(eventType, data);
    }
  };

  // Progressive Disclosure for Interactive Demo
  const handleInteractiveDemoRequest = () => {
    setShowInteractiveDemo(true);
    trackConversionEvent('interactive_demo_requested', {
      calculatorType,
      currentStep,
      hasResults: showResults,
    });
  };

  // Calculate ROI results with enhanced tracking - prioritize real-time results
  const roiResults = useMemo(() => {
    // Use real-time results if available, otherwise calculate
    if (realtimeResults && !showResults) {
      return realtimeResults;
    }

    if (!showResults) return null;

    const results = realtimeResults || calculateROI(calculatorType, inputs);

    // Track ROI calculation completion
    if (results) {
      trackConversionEvent('roi_calculation_completed', {
        calculatorType,
        inputs,
        wasRealtime: !!realtimeResults,
        results: {
          annualSavings: results.annualSavings,
          threeYearROI: results.threeYearROI,
          paybackPeriod: results.paybackPeriod,
        },
      });
    }

    return results;
  }, [calculateROI, calculatorType, inputs, showResults, realtimeResults, trackConversionEvent]);

  // Calculator configuration based on type
  const calculatorConfig = useMemo(() => {
    const configs = {
      'student-performance': {
        title: 'Student Performance ROI Calculator',
        description: 'Calculate the return on investment from improved student outcomes',
        icon: AnalyticsReference,
        color: '#8a3ffc', // Galaxy Purple
        steps: [
          {
            title: 'Current Performance',
            fields: [
              { key: 'currentStudentCount', label: 'Number of Students', min: 1, max: 10000 },
              { key: 'currentGradingHours', label: 'Weekly Grading Hours per Teacher', min: 1, max: 40 },
              { key: 'averageTeacherSalary', label: 'Average Teacher Salary ($)', min: 30000, max: 150000 },
            ],
          },
          {
            title: 'Expected Improvements',
            fields: [
              { key: 'targetImprovement', label: 'Expected Grade Improvement (%)', min: 5, max: 50 },
              { key: 'automationSavings', label: 'Grading Time Reduction (%)', min: 20, max: 80 },
            ],
          },
        ],
      },
      'teacher-productivity': {
        title: 'Teacher Productivity ROI Calculator',
        description: 'Measure productivity gains and time savings for educators',
        icon: Calculator,
        color: '#ffc700', // Saffron Orange
        steps: [
          {
            title: 'Current Workload',
            fields: [
              { key: 'teacherCount', label: 'Number of Teachers', min: 1, max: 1000 },
              { key: 'avgClassSize', label: 'Average Class Size', min: 10, max: 40 },
              { key: 'gradingTimePerStudent', label: 'Grading Minutes per Student', min: 5, max: 30 },
              { key: 'lessonPlanningHours', label: 'Weekly Lesson Planning Hours', min: 3, max: 20 },
            ],
          },
          {
            title: 'Efficiency Gains',
            fields: [
              { key: 'automatedGradingPercent', label: 'Automated Grading (%)', min: 30, max: 90 },
              { key: 'planningEfficiency', label: 'Planning Efficiency Gain (%)', min: 25, max: 75 },
            ],
          },
        ],
      },
      'cost-savings': {
        title: 'Institution Cost Savings Calculator',
        description: 'Analyze potential cost reductions and operational efficiencies',
        icon: AnalyticsReference,
        color: '#0043ce', // Carbon Blue
        steps: [
          {
            title: 'Current Costs',
            fields: [
              { key: 'annualTechnologyBudget', label: 'Annual Technology Budget ($)', min: 10000, max: 5000000 },
              { key: 'textbookCostPerStudent', label: 'Textbook Cost per Student ($)', min: 100, max: 2000 },
              { key: 'professionalDevelopmentCost', label: 'Annual PD Cost ($)', min: 5000, max: 500000 },
            ],
          },
          {
            title: 'Expected Savings',
            fields: [
              { key: 'digitalResourceSavings', label: 'Digital Resource Savings (%)', min: 30, max: 80 },
              { key: 'operationalSavings', label: 'Operational Savings (%)', min: 15, max: 50 },
            ],
          },
        ],
      },
      'learning-outcomes': {
        title: 'Learning Outcomes ROI Calculator',
        description: 'Quantify the impact on student achievement and engagement',
        icon: AnalyticsReference,
        color: '#24a148', // Carbon Green
        steps: [
          {
            title: 'Baseline Metrics',
            fields: [
              { key: 'currentTestScores', label: 'Current Average Test Score (%)', min: 50, max: 100 },
              { key: 'currentEngagement', label: 'Current Engagement Rate (%)', min: 30, max: 100 },
              { key: 'studentCount', label: 'Number of Students', min: 50, max: 10000 },
            ],
          },
          {
            title: 'Target Improvements',
            fields: [
              { key: 'targetImprovement', label: 'Target Score Improvement (%)', min: 5, max: 30 },
              { key: 'engagementIncrease', label: 'Target Engagement Increase (%)', min: 10, max: 50 },
            ],
          },
        ],
      },
      'technology-investment': {
        title: 'Technology Investment ROI Calculator',
        description: 'Evaluate the return on educational technology investments',
        icon: Calculator,
        color: '#e60054', // Hillier Pink
        steps: [
          {
            title: 'Investment Costs',
            fields: [
              { key: 'implementationCost', label: 'Implementation Cost ($)', min: 10000, max: 1000000 },
              { key: 'annualLicenseCost', label: 'Annual License Cost ($)', min: 5000, max: 500000 },
              { key: 'trainingCost', label: 'Training Cost ($)', min: 2000, max: 100000 },
            ],
          },
          {
            title: 'Expected Returns',
            fields: [
              { key: 'efficiencyGains', label: 'Efficiency Gains (%)', min: 20, max: 70 },
              { key: 'costReductions', label: 'Cost Reductions (%)', min: 15, max: 60 },
            ],
          },
        ],
      },
    };

    return configs[calculatorType];
  }, [calculatorType]);

  // Progressive validation function (Epic 18 requirement)
  const validateField = (field: string, value: number): string | null => {
    const fieldConfig = calculatorConfig.steps.flatMap(step => step.fields).find(f => f.key === field);

    if (!fieldConfig) return null;

    if (value < fieldConfig.min) {
      return `Minimum value is ${fieldConfig.min}`;
    }
    if (value > fieldConfig.max) {
      return `Maximum value is ${fieldConfig.max}`;
    }
    return null;
  };

  // Real-time calculation trigger with debouncing
  const triggerRealtimeCalculation = useCallback(
    debounce(async (inputs: ROIInputs) => {
      if (Object.keys(inputs).length === 0) return;

      setIsCalculating(true);
      try {
        const results = await calculateROIRealtime(calculatorType, inputs);
        setRealtimeResults(results);

        // Track real-time calculation event
        trackConversionEvent('realtime_calculation_updated', {
          calculatorType,
          inputFields: Object.keys(inputs).length,
          hasValidInputs: Object.values(validationErrors).every(error => !error),
        });
      } catch (err) {
        console.warn('Real-time calculation failed:', err);
      } finally {
        setIsCalculating(false);
      }
    }, 300),
    [calculatorType, calculateROIRealtime, validationErrors, trackConversionEvent]
  );

  const handleInputChange = (field: string, value: number) => {
    // Update inputs
    const newInputs = {
      ...inputs,
      [field]: value,
    };
    setInputs(newInputs);

    // Progressive validation
    const validationError = validateField(field, value);
    setValidationErrors(prev => ({
      ...prev,
      [field]: validationError || '',
    }));

    // Trigger real-time calculation if no validation errors
    if (!validationError) {
      triggerRealtimeCalculation(newInputs);
    }
  };

  const handleNextStep = () => {
    // Check for validation errors on current step fields
    const currentStepFields = calculatorConfig.steps[currentStep].fields;
    const currentStepErrors = currentStepFields.filter(
      field => validationErrors[field.key] || (!inputs[field.key as keyof ROIInputs] && !field.key.includes('Optional'))
    );

    if (currentStepErrors.length > 0) {
      // Set validation errors for empty required fields
      const newErrors = { ...validationErrors };
      currentStepFields.forEach(field => {
        if (!inputs[field.key as keyof ROIInputs] && !field.key.includes('Optional')) {
          newErrors[field.key] = 'This field is required';
        }
      });
      setValidationErrors(newErrors);
      return;
    }

    if (currentStep < calculatorConfig.steps.length - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      setShowResults(true);

      // Call onComplete callback with results
      if (onComplete) {
        const results = realtimeResults || calculateROI(calculatorType, inputs);
        onComplete(results);
      }
    }
  };

  const handlePrevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  // Enhanced lead capture using v1 API (Epic 18 requirement)
  const handleLeadCapture = async () => {
    try {
      const results = realtimeResults || roiResults;
      if (!results) return;

      // Transform to enhanced lead capture format
      const enhancedLeadData: EducationalLeadCapture = {
        name: leadData.name || '',
        email: leadData.email || '',
        phone: leadData.phone,
        role: selectedRole as 'educator' | 'administrator' | 'parent' | 'student' | 'decision-maker',
        institution: leadData.institution,
        institutionSize: String(inputs.currentStudentCount || inputs.studentCount || 100),
        interestedInDemo: true,
        timeframe: 'immediate',
        calculatorType,
        calculatorResults: results,
        budget: 'unspecified',
        currentSolutions: [],
      };

      // Submit to v1 lead capture API
      const response = await fetch('/api/v1/calculators/capture-lead', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(enhancedLeadData),
      });

      if (response.ok) {
        // Track successful lead capture
        trackConversionEvent('enhanced_lead_captured', {
          calculatorType,
          role: enhancedLeadData.role,
          institutionSize: enhancedLeadData.institutionSize,
          interestedInDemo: enhancedLeadData.interestedInDemo,
        });

        // Call parent callback if provided
        if (onLeadCapture) {
          onLeadCapture({
            ...leadData,
            calculatorType,
            roiResults: results,
          } as LeadCaptureData);
        }
      } else {
        throw new Error('Lead capture failed');
      }
    } catch (error) {
      console.error('Enhanced lead capture failed:', error);
      // Fallback to legacy lead capture
      if (onLeadCapture && (realtimeResults || roiResults)) {
        onLeadCapture({
          ...leadData,
          calculatorType,
          roiResults: realtimeResults || roiResults,
        } as LeadCaptureData);
      }
    }

    setShowLeadCapture(false);
  };

  const handleDemoRequest = () => {
    if (onDemoRequest) {
      onDemoRequest({
        ...demoData,
        calculatorType,
      } as DemoRequestData);
    }
    setShowDemoRequest(false);
  };

  // PDF Report Generation (Epic 18 requirement)
  const handlePDFDownload = async () => {
    try {
      const results = realtimeResults || roiResults;
      if (!results) return;

      const reportData = {
        calculatorType,
        results,
        inputs,
        timestamp: new Date(),
        userInfo: {
          name: leadData.name,
          institution: leadData.institution,
          role: selectedRole,
        },
      };

      await generateAndDownloadReport(reportData);

      // Track PDF download
      trackConversionEvent('pdf_report_downloaded', {
        calculatorType,
        hasUserInfo: !!(leadData.name || leadData.institution),
        role: selectedRole,
      });
    } catch (error) {
      console.error('PDF generation failed:', error);
      // Could show error notification here
    }
  };

  const currentStepData = calculatorConfig.steps[currentStep];
  const IconComponent = calculatorConfig.icon;

  if (showResults && roiResults) {
    return (
      <section className={styles.calculatorSection}>
        <Grid>
          <Column lg={16} md={8} sm={4}>
            <Tile className={styles.calculatorTile}>
              <Stack gap={7}>
                {/* Results Header */}
                <div className={styles.calculatorHeader}>
                  <div className={styles.iconWrapper}>
                    <IconComponent size={24} className={styles.iconWrapper} />
                  </div>
                  <div>
                    <Heading className={styles.calculatorTitle}>Your ROI Results</Heading>
                    <p className={styles.calculatorDescription}>{calculatorConfig.title}</p>
                  </div>
                </div>

                {/* Key Metrics */}
                <Grid condensed>
                  <Column lg={4} md={2} sm={2}>
                    <Tile className={styles.livePreviewTile}>
                      <div className={styles.metricValue}>{roiResults.threeYearROI}%</div>
                      <div className={styles.metricLabel}>3-Year ROI</div>
                    </Tile>
                  </Column>

                  <Column lg={4} md={2} sm={2}>
                    <Tile className={styles.previewMetric}>
                      <div className={styles.previewMetricValue}>${roiResults.annualSavings.toLocaleString()}</div>
                      <div className={styles.previewMetricLabel}>Annual Savings</div>
                    </Tile>
                  </Column>

                  <Column lg={4} md={2} sm={2}>
                    <Tile className={styles.previewMetric}>
                      <div className={styles.previewMetricValue}>{roiResults.paybackPeriod}</div>
                      <div className={styles.previewMetricLabel}>Months to Payback</div>
                    </Tile>
                  </Column>

                  <Column lg={4} md={2} sm={2}>
                    <Tile className={styles.previewMetric}>
                      <div className={styles.previewMetricValue}>${roiResults.costPerStudent}</div>
                      <div className={styles.previewMetricLabel}>Cost per Student</div>
                    </Tile>
                  </Column>
                </Grid>

                {/* ROI Visualization */}
                <ROIVisualization data={roiResults} calculatorType={calculatorType} height={400} />

                {/* Detailed Breakdown */}
                <Accordion>
                  <AccordionItem title="Detailed ROI Breakdown">
                    <Grid>
                      {Object.entries(roiResults.breakdownByCategory).map(([category, value]) => (
                        <Column key={category} lg={8} md={4} sm={2}>
                          <div className={styles.breakdownItem}>
                            <span className={styles.breakdownLabel}>
                              {category.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                            </span>
                            <Tag type="outline">${value.toLocaleString()}</Tag>
                          </div>
                        </Column>
                      ))}
                    </Grid>
                  </AccordionItem>
                </Accordion>

                {/* Action Buttons */}
                <div className={styles.actionButtons}>
                  <Button kind="primary" size="lg" renderIcon={Email} onClick={() => setShowLeadCapture(true)}>
                    Get Detailed ROI Report
                  </Button>

                  <Button kind="secondary" size="lg" renderIcon={Calendar} onClick={() => setShowDemoRequest(true)}>
                    Schedule Demo
                  </Button>

                  <Button kind="tertiary" size="lg" renderIcon={Download} onClick={handlePDFDownload}>
                    Download PDF Report
                  </Button>
                </div>
              </Stack>
            </Tile>
          </Column>
        </Grid>

        {/* Lead Capture Modal */}
        <Modal
          open={showLeadCapture}
          onRequestClose={() => setShowLeadCapture(false)}
          modalHeading="Get Your Detailed ROI Report"
          modalLabel="Educational ROI Analysis"
          primaryButtonText="Send Report"
          secondaryButtonText="Cancel"
          onRequestSubmit={handleLeadCapture}
        >
          <Form>
            <Stack gap={5}>
              <TextInput
                id="lead-name"
                labelText="Full Name *"
                placeholder="Enter your full name"
                value={leadData.name || ''}
                onChange={e => setLeadData(prev => ({ ...prev, name: e.target.value }))}
                required
              />

              <TextInput
                id="lead-email"
                labelText="Email Address *"
                placeholder="Enter your email"
                value={leadData.email || ''}
                onChange={e => setLeadData(prev => ({ ...prev, email: e.target.value }))}
                required
              />

              <Select
                id="lead-role"
                labelText="Role *"
                value={selectedRole}
                onChange={e => setSelectedRole(e.target.value)}
                required
              >
                <SelectItem value="" text="Select your role" />
                <SelectItem value="educator" text="Teacher/Educator" />
                <SelectItem value="administrator" text="Administrator/Principal" />
                <SelectItem value="decision-maker" text="IT Director/Decision Maker" />
                <SelectItem value="parent" text="Parent" />
                <SelectItem value="student" text="Student" />
              </Select>

              <TextInput
                id="lead-title"
                labelText="Job Title (Optional)"
                placeholder="e.g., Principal, IT Director"
                value={leadData.title || ''}
                onChange={e => setLeadData(prev => ({ ...prev, title: e.target.value }))}
              />

              <TextInput
                id="lead-institution"
                labelText="Institution *"
                placeholder="Enter school or district name"
                value={leadData.institution || ''}
                onChange={e => setLeadData(prev => ({ ...prev, institution: e.target.value }))}
                required
              />

              <TextInput
                id="lead-phone"
                labelText="Phone Number (Optional)"
                placeholder="Enter phone number"
                value={leadData.phone || ''}
                onChange={e => setLeadData(prev => ({ ...prev, phone: e.target.value }))}
              />
            </Stack>
          </Form>
        </Modal>

        {/* Demo Request Modal */}
        <Modal
          open={showDemoRequest}
          onRequestClose={() => setShowDemoRequest(false)}
          modalHeading="Schedule Your Demo"
          modalLabel="Personalized Demonstration"
          primaryButtonText="Schedule Demo"
          secondaryButtonText="Cancel"
          onRequestSubmit={handleDemoRequest}
        >
          <Form>
            <Stack gap={5}>
              <TextInput
                id="demo-name"
                labelText="Full Name *"
                placeholder="Enter your full name"
                value={demoData.name || ''}
                onChange={e => setDemoData(prev => ({ ...prev, name: e.target.value }))}
                required
              />

              <TextInput
                id="demo-email"
                labelText="Email Address *"
                placeholder="Enter your email"
                value={demoData.email || ''}
                onChange={e => setDemoData(prev => ({ ...prev, email: e.target.value }))}
                required
              />

              <TextInput
                id="demo-institution"
                labelText="Institution *"
                placeholder="Enter school or district name"
                value={demoData.institution || ''}
                onChange={e => setDemoData(prev => ({ ...prev, institution: e.target.value }))}
                required
              />

              <TextInput
                id="demo-date"
                labelText="Preferred Date"
                placeholder="Enter preferred date and time"
                value={demoData.preferredDate || ''}
                onChange={e => setDemoData(prev => ({ ...prev, preferredDate: e.target.value }))}
              />
            </Stack>
          </Form>
        </Modal>
      </section>
    );
  }

  return (
    <section className={styles.calculatorSection}>
      <Grid>
        <Column lg={16} md={8} sm={4}>
          <Tile className={styles.calculatorTile}>
            <Stack gap={7}>
              {/* Calculator Header */}
              <div className={styles.calculatorHeader}>
                <div className={styles.iconWrapper}>
                  <IconComponent size={24} className={styles.iconWrapper} />
                </div>
                <div>
                  <Heading className={styles.calculatorTitle}>{calculatorConfig.title}</Heading>
                  <p className={styles.calculatorDescription}>{calculatorConfig.description}</p>
                </div>
              </div>

              {/* Progress Indicator */}
              <ProgressIndicator currentIndex={currentStep}>
                {calculatorConfig.steps.map((step, index) => (
                  <ProgressStep
                    key={index}
                    label={step.title}
                    complete={index < currentStep}
                    current={index === currentStep}
                  />
                ))}
                <ProgressStep label="Results" complete={showResults} current={false} />
              </ProgressIndicator>

              {/* Error Display */}
              {error && <InlineNotification title="Calculation Error" subtitle={error} kind="error" lowContrast />}

              {/* Current Step Form */}
              <Form>
                <FormGroup legendText={currentStepData.title}>
                  <Grid>
                    {currentStepData.fields.map(field => (
                      <Column key={field.key} lg={8} md={4} sm={4}>
                        <NumberInput
                          id={field.key}
                          label={field.label}
                          value={inputs[field.key as keyof ROIInputs] || field.min}
                          min={field.min}
                          max={field.max}
                          step={field.key.includes('Percent') ? 1 : field.min < 100 ? 1 : 100}
                          onChange={(e, { value }) => handleInputChange(field.key, value as number)}
                          invalid={!!validationErrors[field.key]}
                          invalidText={validationErrors[field.key]}
                          helperText={
                            field.key.includes('Percent')
                              ? `Range: ${field.min}% - ${field.max}%`
                              : `Range: ${field.min.toLocaleString()} - ${field.max.toLocaleString()}`
                          }
                        />
                      </Column>
                    ))}
                  </Grid>
                </FormGroup>
              </Form>

              {/* Real-time Preview (Epic 18 feature) */}
              {realtimeResults && !showResults && (
                <Tile className={styles.livePreviewTile}>
                  <div className={styles.livePreviewHeader}>
                    <Calculator size={20} />
                    <span className={styles.livePreviewLabel}>Live Preview</span>
                    {isCalculating && <div className={styles.loadingSpinner} />}
                  </div>
                  <Grid condensed>
                    <Column lg={4} md={2} sm={2}>
                      <div className={styles.previewMetricValue}>{realtimeResults.threeYearROI}%</div>
                      <div className={styles.previewMetricLabel}>3-Year ROI</div>
                    </Column>
                    <Column lg={4} md={2} sm={2}>
                      <div className={styles.previewMetricValue}>${realtimeResults.annualSavings.toLocaleString()}</div>
                      <div className={styles.previewMetricLabel}>Annual Savings</div>
                    </Column>
                    <Column lg={4} md={2} sm={2}>
                      <div className={styles.previewMetricValue}>{realtimeResults.paybackPeriod} months</div>
                      <div className={styles.previewMetricLabel}>Payback Period</div>
                    </Column>
                    <Column lg={4} md={2} sm={2}>
                      <div className={styles.previewMetricValue}>${realtimeResults.costPerStudent}</div>
                      <div className={styles.previewMetricLabel}>Cost per Student</div>
                    </Column>
                  </Grid>
                </Tile>
              )}

              {/* Navigation Buttons */}
              <div className={styles.navigationButtons}>
                <Button kind="secondary" disabled={currentStep === 0} onClick={handlePrevStep}>
                  Previous
                </Button>

                <Button
                  kind="primary"
                  renderIcon={currentStep === calculatorConfig.steps.length - 1 ? Calculator : ArrowRight}
                  onClick={handleNextStep}
                  disabled={loading}
                >
                  {currentStep === calculatorConfig.steps.length - 1 ? 'Calculate ROI' : 'Next'}
                </Button>
              </div>
            </Stack>
          </Tile>
        </Column>
      </Grid>
    </section>
  );
};

export default EducationalROICalculator;
